import { apiClient } from '../../../lib/apiClient';
import type { AxiosRequestConfig } from 'axios';

export interface PriceVariation {
  diff: number;
  pct: number;
  trend: 'up' | 'down' | 'equal';
  previous_price: number;
  current_price: number;
}

export interface InventoryItem {
  id: string | number;
  sku: string;
  name: string;
  category?: string;
  supplier_id?: number | null;
  unit?: string;
  cost?: number;
  min_stock?: number;
  minStock?: number;
  optimal_stock?: number;
  purchase_unit?: string;
  conversion_factor?: number;
  purchase_price?: number | null;
  last_purchase_price?: number | null;
  purchase_presentation_label?: string | null;
  price_variation?: PriceVariation | null;
  current_stock?: number;
  locations?: string[];
  notes?: string;
  is_subrecipe?: boolean;
  createdAt?: string;
  updatedAt?: string;
}
export interface StockMovement { id: string; itemId: string; type: 'IN' | 'OUT' | 'ADJUST' | 'TRANSFER'; qty: number; fromLocation?: string; toLocation?: string; reason?: string; createdAt?: string; }
export interface Paginated<T> { data: T[]; total: number; page?: number; limit?: number; }
export interface StockResult { itemId: string; location?: string; onHand: number; item?: InventoryItem; }
export interface StockSummaryItem { label: string; total: number; }

export interface CostHistoryItem {
  id: number | string;
  inventory_item_id: number | string;
  old_cost: number | null;
  new_cost: number;
  reason?: string | null;
  created_at: string;
  user?: { id?: number; name?: string } | null;
}

const withConfig = (config?: AxiosRequestConfig) => ({ ...config, headers: config?.headers });

export const createItem = async (data: Partial<InventoryItem>) => (await apiClient.post<InventoryItem>('/inventory/items', data, withConfig())).data;
export const listItems = async (params?: Record<string, unknown>) => (await apiClient.get<Paginated<InventoryItem>>('/inventory/items', { params })).data;
export const getItem = async (id: string) => (await apiClient.get<InventoryItem>(`/inventory/items/${id}`)).data;
export const updateItem = async (id: string, data: Partial<InventoryItem>) => (await apiClient.patch<InventoryItem>(`/inventory/items/${id}`, data, withConfig())).data;
export const deleteItem = async (id: string) => (await apiClient.delete(`/inventory/items/${id}`, withConfig())).data;
export const createMovement = async (data: Partial<StockMovement>) => (await apiClient.post<StockMovement>('/inventory/movements', data, withConfig())).data;
export const listMovements = async (params?: Record<string, unknown>) => (await apiClient.get<Paginated<StockMovement>>('/inventory/movements', { params })).data;
export const getStock = async (params?: Record<string, unknown>) => (await apiClient.get<StockResult[]>('/inventory/stock', { params })).data;
export const getStockSummary = async (params?: Record<string, unknown>) => (await apiClient.get<StockSummaryItem[]>('/inventory/stock/summary', { params })).data;
export const getCostHistory = async (itemId: string | number) => (await apiClient.get<CostHistoryItem[]>(`/inventory/items/${itemId}/cost-history`, withConfig())).data;
export const adjustStock = async (itemId: string | number, data: { new_stock: number; reason?: string; type?: string }) => (await apiClient.post<{ message: string; current_stock: number }>(`/inventory/items/${itemId}/adjust-stock`, data, withConfig())).data;

export interface InventoryCategory {
  id: number;
  name: string;
  items_count?: number;
}

export const listCategories = async () => (await apiClient.get<InventoryCategory[]>('/inventory/categories', withConfig())).data;
export const createCategory = async (data: { name: string }) => (await apiClient.post<InventoryCategory>('/inventory/categories', data, withConfig())).data;
export const updateCategory = async (id: number | string, data: { name: string }) => (await apiClient.patch<InventoryCategory>(`/inventory/categories/${id}`, data, withConfig())).data;
export const deleteCategory = async (id: number | string) => (await apiClient.delete(`/inventory/categories/${id}`, withConfig())).data;

// ── Auditoría Física Masiva de Stock (Toma de Inventario) ───────────────────
export interface StockAuditItemInput {
  inventory_item_id: number | string;
  counted_stock: number;
  reason?: string;
  notes?: string;
}

export interface StockAuditPayload {
  category?: string;
  notes?: string;
  register_as_expense?: boolean;
  deduct_from_cash?: boolean;
  supplier_id?: number;
  invoice_number?: string;
  items: StockAuditItemInput[];
}

export interface InventoryAuditSummary {
  id: number;
  audit_code: string;
  category: string;
  total_items_counted: number;
  items_with_discrepancy: number;
  net_cost_difference: number;
  positive_cost_difference: number;
  negative_cost_difference: number;
  status: string;
  notes?: string;
  created_at: string;
  user?: { id: number; name: string };
  items_count?: number;
}

export const auditStock = async (data: StockAuditPayload) =>
  (await apiClient.post<{ message: string; audit: any }>('/inventory/audit', data, withConfig())).data;

export const listAudits = async () =>
  (await apiClient.get<InventoryAuditSummary[]>('/inventory/audits', withConfig())).data;

export const getAudit = async (id: number | string) =>
  (await apiClient.get<any>(`/inventory/audits/${id}`, withConfig())).data;

// ── Kardex y Trazabilidad de Insumo ──────────────────────────────────────────
export interface ItemKardexMovement {
  id: number;
  moved_at: string | null;
  type: string;
  qty: number;
  signed_qty: number;
  unit: string;
  stock_before: number;
  stock_after: number;
  reason?: string;
  reference?: string;
  category_tag: string;
  icon: string;
  color: string;
  user_id?: number | null;
}

export interface ItemKardexResponse {
  item: {
    id: number;
    name: string;
    sku: string;
    category?: string;
    unit: string;
    cost: number;
    min_stock: number;
    optimal_stock?: number;
    supplier?: any;
    current_stock: number;
    stock_value: number;
  };
  summary: {
    total_in: number;
    total_out: number;
    net_balance: number;
    movements_count: number;
  };
  movements: ItemKardexMovement[];
}

export const getItemKardex = async (itemId: number | string, params?: Record<string, unknown>) =>
  (await apiClient.get<ItemKardexResponse>(`/inventory/items/${itemId}/kardex`, { params, ...withConfig() })).data;

// ── Sugerencia Automática de Compras (Reabastecimiento) ──────────────────────
export interface ReplenishmentSuggestionItem {
  inventory_item_id: number;
  name: string;
  sku: string;
  category?: string;
  unit: string;
  unit_cost: number;
  current_stock: number;
  min_stock: number;
  optimal_stock?: number | null;
  suggested_qty: number;
  estimated_total: number;
  supplier_id?: number | null;
  supplier_name: string;
  supplier?: any;
}

export interface ReplenishmentSupplierGroup {
  supplier_id?: number | null;
  supplier_name: string;
  supplier?: any;
  total_cost: number;
  items_count: number;
  items: ReplenishmentSuggestionItem[];
}

export interface ReplenishmentSuggestionsResponse {
  total_items_to_order: number;
  total_estimated_cost: number;
  suppliers_groups: ReplenishmentSupplierGroup[];
  all_suggestions: ReplenishmentSuggestionItem[];
}

export const getReplenishmentSuggestions = async () =>
  (await apiClient.get<ReplenishmentSuggestionsResponse>('/inventory/replenishment-suggestions', withConfig())).data;

export const generateSuggestedOrders = async (payload: { orders: any[] }) =>
  (await apiClient.post<{ message: string; orders: any[] }>('/inventory/generate-suggested-orders', payload, withConfig())).data;

// ── Sub-recetas y Producción Interna (Mise en Place) ─────────────────────────
export interface SubRecipeIngredient {
  id?: number;
  sub_recipe_id?: number;
  inventory_item_id: number;
  qty: number;
  unit: string;
  waste_pct?: number;
  notes?: string;
  inventory_item?: any;
}

export interface SubRecipe {
  id: number;
  name: string;
  inventory_item_id: number;
  yield_qty: number;
  yield_unit: string;
  prep_time_minutes?: number | null;
  instructions?: string | null;
  active: boolean;
  theoretical_cost_per_unit?: number;
  theoretical_batch_cost?: number;
  ingredients: SubRecipeIngredient[];
  produced_item?: any;
  batches?: any[];
}

export const listSubRecipes = async () =>
  (await apiClient.get<SubRecipe[]>('/inventory/sub-recipes', withConfig())).data;

export const createSubRecipe = async (data: any) =>
  (await apiClient.post<{ message: string; sub_recipe: SubRecipe }>('/inventory/sub-recipes', data, withConfig())).data;

export const updateSubRecipe = async (id: number | string, data: any) =>
  (await apiClient.put<{ message: string; sub_recipe: SubRecipe }>(`/inventory/sub-recipes/${id}`, data, withConfig())).data;

export const deleteSubRecipe = async (id: number | string) =>
  (await apiClient.delete<{ message: string }>(`/inventory/sub-recipes/${id}`, withConfig())).data;

export const produceSubRecipeBatch = async (id: number | string, data: { batch_qty: number; notes?: string }) =>
  (await apiClient.post<{ message: string; batch: any }>(`/inventory/sub-recipes/${id}/produce`, data, withConfig())).data;

// ── Control Rápido de Mermas y Desperdicios (Waste Log) ───────────────────────
export interface InventoryWaste {
  id: number;
  inventory_item_id: number;
  qty: number;
  unit: string;
  unit_cost: number;
  total_cost: number;
  reason: string;
  responsible_name?: string | null;
  user_id?: number | null;
  notes?: string | null;
  wasted_at: string;
  item?: any;
  user?: { id: number; name: string };
}

export interface WasteLogResponse {
  total_events: number;
  total_loss_cost: number;
  by_reason: Record<string, number>;
  data: InventoryWaste[];
}

export const createWaste = async (data: {
  inventory_item_id: number | string;
  qty: number;
  unit?: string;
  reason: string;
  responsible_name?: string;
  notes?: string;
  wasted_at?: string;
}) => (await apiClient.post<{ message: string; waste: InventoryWaste }>('/inventory/wastes', data, withConfig())).data;

export const listWastes = async (params?: Record<string, unknown>) =>
  (await apiClient.get<WasteLogResponse>('/inventory/wastes', { params, ...withConfig() })).data;



